/*
 * er-gallery.js — Easy Rust product "Inspection Console" lightbox.
 *
 * Idempotent + re-render safe (see Phase 0 notes): a single guarded overlay,
 * thumbnail clicks via event delegation on `document`, image list rebuilt from
 * the live DOM on each open. Dawn re-rendering the section can never duplicate
 * the overlay or drop the click handlers.
 *
 * Phase 4 upgrades:
 *   - macOS "window" chrome around the image (ties into the er-system-info card)
 *   - directional expand-from-thumb entrance (transform-origin from the tile)
 *   - cursor-anchored click-to-zoom (pointer sets transform-origin; touch-safe)
 *   - filmstrip of mini-thumbs (replaces bare dots)
 *   - 3D tilt on the window (suspended while zoomed)
 *   - focus trap + focus restore for keyboard/a11y
 * All motion is skipped under prefers-reduced-motion; zoom stays (it's a utility).
 */
(function () {
  'use strict';
  if (window.__erGalleryInit) return;
  window.__erGalleryInit = true;

  var overlay, stage, frame, bar, hint, gloss, content, img, prevBtn, nextBtn, closeBtn, strip, counterCur, counterTot;
  var HINT_ZOOM_IN = 'click to zoom';
  var HINT_ZOOM_OUT = 'click to zoom out';
  var srcs = [];
  var currentIdx = 0;
  var opener = null;          // thumb that opened the lightbox (focus restore)
  var tiltEnabled = false;
  var isZoomed = false;
  var TILT_DEG = 8; // max tilt each axis — gentle "look toward the cursor" parallax
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = !(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);

  var PREV_SVG = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>';
  var NEXT_SVG = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>';
  var CLOSE_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  function ensureOverlay() {
    if (overlay) return overlay;
    overlay = document.querySelector('.er-lightbox-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'er-lightbox-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Product preview');
      overlay.innerHTML =
        '<div class="er-lightbox-stage">' +
          '<button type="button" class="er-lb-nav er-lb-nav--prev" aria-label="Previous image">' + PREV_SVG + '</button>' +
          '<div class="er-lb-frame">' +
            '<div class="er-lb-bar">' +
              '<span class="er-lb-tl er-lb-tl--r" aria-hidden="true"></span>' +
              '<span class="er-lb-tl er-lb-tl--y" aria-hidden="true"></span>' +
              '<span class="er-lb-tl er-lb-tl--g" aria-hidden="true"></span>' +
              '<span class="er-lb-bar-title">easy rust ~ preview.png</span>' +
            '</div>' +
            '<div class="er-lightbox-content">' +
              '<img class="er-lightbox-img" src="" alt="Product screenshot">' +
            '</div>' +
            '<div class="er-lb-gloss" aria-hidden="true"></div>' +
          '</div>' +
          '<button type="button" class="er-lb-nav er-lb-nav--next" aria-label="Next image">' + NEXT_SVG + '</button>' +
          '<div class="er-lb-strip" data-er-strip></div>' +
          '<div class="er-lb-counter"><span data-er-current>1</span> / <span data-er-total>1</span></div>' +
        '</div>';
      document.body.appendChild(overlay);
    }

    stage = overlay.querySelector('.er-lightbox-stage');
    frame = overlay.querySelector('.er-lb-frame');
    bar = overlay.querySelector('.er-lb-bar');
    hint = overlay.querySelector('.er-lb-bar-hint');
    gloss = overlay.querySelector('.er-lb-gloss');
    content = overlay.querySelector('.er-lightbox-content');
    img = overlay.querySelector('.er-lightbox-img');
    prevBtn = overlay.querySelector('.er-lb-nav--prev');
    nextBtn = overlay.querySelector('.er-lb-nav--next');
    strip = overlay.querySelector('[data-er-strip]');
    counterCur = overlay.querySelector('[data-er-current]');
    counterTot = overlay.querySelector('[data-er-total]');

    closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'er-lightbox-close';
    closeBtn.setAttribute('aria-label', 'Close preview');
    closeBtn.innerHTML = CLOSE_SVG;
    stage.appendChild(closeBtn);

    wire();
    return overlay;
  }

  function wire() {
    prevBtn.addEventListener('click', function (e) { e.stopPropagation(); showImage(currentIdx - 1); });
    nextBtn.addEventListener('click', function (e) { e.stopPropagation(); showImage(currentIdx + 1); });
    closeBtn.addEventListener('click', function (e) { e.stopPropagation(); close(); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay || e.target === stage) close(); });

    // Click image → toggle cursor-anchored zoom. Works for every pointer type
    // (touch taps carry clientX/Y too); origin falls back to center when absent.
    content.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleZoom(e);
    });
    content.addEventListener('mousemove', function (e) {
      if (!isZoomed) return;
      var r = content.getBoundingClientRect();
      content.style.setProperty('--zx', (((e.clientX - r.left) / r.width) * 100) + '%');
      content.style.setProperty('--zy', (((e.clientY - r.top) / r.height) * 100) + '%');
    });

    // 3D cursor-follow tilt: driven from the WHOLE overlay so ANY mouse movement over
    // the lightbox turns the window to face the cursor, with a specular gloss tracking
    // the pointer so the 3D read is unmistakable. Suspended while zoomed.
    overlay.addEventListener('mousemove', onTiltMove);
    overlay.addEventListener('mouseleave', onTiltLeave);

    document.addEventListener('keydown', function (e) {
      if (!overlay.classList.contains('active')) return;
      if (e.key === 'ArrowLeft') { showImage(currentIdx - 1); }
      else if (e.key === 'ArrowRight') { showImage(currentIdx + 1); }
      else if (e.key === 'Escape') { close(); }
      else if (e.key === '+' || e.key === '=') { if (finePointer && !isZoomed) toggleZoom(null); }
      else if (e.key === '-') { if (isZoomed) toggleZoom(null); }
      else if (e.key === 'Tab') { trapFocus(e); }
    });
  }

  // Rebuild srcs + filmstrip from the CURRENT thumbnails (picks up variant re-renders).
  function syncFromDom() {
    var thumbs = document.querySelectorAll('.er-thumb-item img');
    srcs = [];
    // Request a higher-res source so the enlarged console + click-to-zoom stay crisp.
    thumbs.forEach(function (t) { srcs.push(t.src.replace(/width=\d+/, 'width=1600')); });

    overlay.className = 'er-lightbox-overlay';
    overlay.setAttribute('role', 'dialog');
    var section = document.querySelector('.er-thumb-section');
    if (section) {
      var handleClass = Array.prototype.find.call(section.classList, function (c) { return c.indexOf('er-thumb-easy-rust-') === 0; });
      if (handleClass) overlay.classList.add('er-lb-' + handleClass.replace('er-thumb-', ''));
    }

    strip.innerHTML = '';
    for (var i = 0; i < srcs.length; i++) {
      var t = document.createElement('button');
      t.type = 'button';
      t.className = 'er-lb-thumb' + (i === 0 ? ' is-active' : '');
      t.setAttribute('aria-label', 'View image ' + (i + 1));
      var im = document.createElement('img');
      im.src = srcs[i].replace(/width=\d+/, 'width=120');
      im.alt = '';
      t.appendChild(im);
      (function (idx) { t.addEventListener('click', function (e) { e.stopPropagation(); showImage(idx); }); })(i);
      strip.appendChild(t);
    }
    if (counterTot) counterTot.textContent = srcs.length;
    strip.style.display = srcs.length > 1 ? '' : 'none';
    prevBtn.style.display = nextBtn.style.display = srcs.length > 1 ? '' : 'none';
  }

  function updateIndicator(idx) {
    var thumbs = strip.querySelectorAll('.er-lb-thumb');
    for (var k = 0; k < thumbs.length; k++) thumbs[k].classList.toggle('is-active', k === idx);
    if (counterCur) counterCur.textContent = idx + 1;
    var active = thumbs[idx];
    if (active && active.scrollIntoView) active.scrollIntoView({ block: 'nearest', inline: 'center' });
  }

  function showImage(idx) {
    if (!srcs.length) return;
    if (isZoomed) unzoom();
    currentIdx = (idx + srcs.length) % srcs.length;
    img.classList.add('fade-out');
    setTimeout(function () {
      img.src = srcs[currentIdx];
      img.classList.remove('fade-out');
    }, 180);
    updateIndicator(currentIdx);
  }

  function open(idx, thumbEl) {
    ensureOverlay();
    syncFromDom();
    if (!srcs.length) return;
    opener = thumbEl || null;
    currentIdx = (idx + srcs.length) % srcs.length;
    img.src = srcs[currentIdx];
    updateIndicator(currentIdx);

    // Directional expand: origin toward the clicked tile (skipped for reduced motion).
    if (!reduceMotion && thumbEl && frame) {
      var r = thumbEl.getBoundingClientRect();
      var ox = ((r.left + r.width / 2) / window.innerWidth) * 100;
      var oy = ((r.top + r.height / 2) / window.innerHeight) * 100;
      frame.style.transformOrigin = ox + '% ' + oy + '%';
    } else if (frame) {
      frame.style.transformOrigin = '50% 50%';
    }

    setHint(HINT_ZOOM_IN);
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    tiltEnabled = finePointer && !reduceMotion;
    // focus the close button for keyboard users
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    if (isZoomed) unzoom();
    resetTilt(0);
    if (overlay) overlay.classList.remove('active');
    document.body.style.overflow = '';
    if (opener && opener.focus) { try { opener.focus(); } catch (e) {} }
  }

  /* ---- zoom ---- */
  function setHint(t) { if (hint) hint.textContent = t; }
  function toggleZoom(e) { if (isZoomed) unzoom(); else zoom(e); }
  function zoom(e) {
    isZoomed = true;
    tiltEnabled = false;
    resetTilt(0.25);
    if (e && typeof e.clientX === 'number') {
      var r = content.getBoundingClientRect();
      content.style.setProperty('--zx', (((e.clientX - r.left) / r.width) * 100) + '%');
      content.style.setProperty('--zy', (((e.clientY - r.top) / r.height) * 100) + '%');
    } else {
      content.style.setProperty('--zx', '50%');
      content.style.setProperty('--zy', '50%');
    }
    content.classList.add('is-zoomed');
    setHint(HINT_ZOOM_OUT);
  }
  function unzoom() {
    isZoomed = false;
    content.classList.remove('is-zoomed');
    tiltEnabled = finePointer && !reduceMotion;
    setHint(HINT_ZOOM_IN);
  }

  /* ---- tilt (frame only) ---- */
  function onTiltMove(e) {
    if (!tiltEnabled || isZoomed || reduceMotion || !stage) return;
    var r = stage.getBoundingClientRect();
    // Center is stable under a centered rotation; untransformed layout size as the divisor
    // so the tilt fraction doesn't drift as the stage rotates.
    var cx = r.left + r.width / 2;
    var cy = r.top + r.height / 2;
    var halfW = (stage.offsetWidth || r.width) / 2;
    var halfH = (stage.offsetHeight || r.height) / 2;
    var dx = Math.max(-1, Math.min(1, (e.clientX - cx) / halfW));
    var dy = Math.max(-1, Math.min(1, (e.clientY - cy) / halfH));
    // Tilt the WHOLE stage (window + < > × + filmstrip) as ONE 3D plane toward the cursor.
    // Slow 0.3s ease + a gentle ±8° so it glides rather than snapping.
    stage.style.transformOrigin = '50% 50%';
    stage.style.transition = 'transform 0.3s ease-out';
    stage.style.transform = 'perspective(1400px) rotateX(' + (-dy * TILT_DEG).toFixed(2) + 'deg) rotateY(' + (dx * TILT_DEG).toFixed(2) + 'deg)';
    if (gloss) {
      gloss.style.setProperty('--gx', (dx * 50 + 50).toFixed(1) + '%');
      gloss.style.setProperty('--gy', (dy * 50 + 50).toFixed(1) + '%');
      frame.classList.add('is-tilting');
    }
  }
  function onTiltLeave() { if (frame) frame.classList.remove('is-tilting'); resetTilt(0.55); }
  function resetTilt(sec) {
    if (!stage) return;
    stage.style.transition = 'transform ' + (sec || 0.4) + 's ease';
    stage.style.transform = '';
    if (frame) frame.classList.remove('is-tilting');
  }

  /* ---- focus trap ---- */
  function trapFocus(e) {
    // Collect the visible controls in DOM order (= tab order), so the first/last
    // boundaries actually match how the browser moves focus. Ordering them any
    // other way lets Tab escape the dialog from the DOM-last control.
    var f = Array.prototype.slice.call(overlay.querySelectorAll('button')).filter(function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || f.indexOf(active) === -1)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (active === last || f.indexOf(active) === -1)) { e.preventDefault(); first.focus(); }
  }

  // Delegated open — survives any number of Dawn re-renders.
  function openFromThumb(thumb) {
    var items = Array.prototype.slice.call(document.querySelectorAll('.er-thumb-item'));
    var idx = items.indexOf(thumb);
    open(idx < 0 ? 0 : idx, thumb);
  }
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest && e.target.closest('.er-thumb-item');
    if (thumb) openFromThumb(thumb);
  });
  // Keyboard open (thumbs are role="button" tabindex="0").
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var thumb = e.target.closest && e.target.closest('.er-thumb-item');
    if (thumb) { e.preventDefault(); openFromThumb(thumb); }
  });
})();
